namespace $ {
	
	export type $rise_baza_patch_land_policy = {
		persistent?: boolean
		signed?: boolean
	}
	
	export const $rise_baza_patch_policy_fast: Required< $rise_baza_patch_land_policy > = {
		persistent: false,
		signed: false,
	}
	
	export const $rise_baza_patch_policy_temp_signed: Required< $rise_baza_patch_land_policy > = {
		persistent: false,
		signed: true,
	}
	
	export const $rise_baza_patch_policy_fast_peer = 'RISEFAST'
	export const $rise_baza_patch_policy_temp_signed_area_prefix = 'Æ'
	
	export const $rise_baza_patch_land_policies = new Map< string, Required< $rise_baza_patch_land_policy > >()
	
	export function $rise_baza_patch_land_policy_default( key: string ) {
		if( key === $rise_baza_patch_policy_fast_peer || key.startsWith( `${ $rise_baza_patch_policy_fast_peer }_` ) ) {
			return $rise_baza_patch_policy_fast
		}
		
		try {
			const land = new $giper_baza_link( key ).land()
			if( land.peer().str === $rise_baza_patch_policy_fast_peer ) {
				return $rise_baza_patch_policy_fast
			}
			if( land.area().str.startsWith( $rise_baza_patch_policy_temp_signed_area_prefix ) ) {
				return $rise_baza_patch_policy_temp_signed
			}
		} catch {}
		
		return {
			persistent: true,
			signed: true,
		}
	}
	
	export function $rise_baza_patch_land_policy_key(
		key: string,
		next?: $rise_baza_patch_land_policy,
	) {
		const prev = $rise_baza_patch_land_policies.get( key ) ?? $rise_baza_patch_land_policy_default( key )
		
		if( next ) {
			$rise_baza_patch_land_policies.set( key, {
				persistent: next.persistent ?? prev.persistent,
				signed: next.signed ?? prev.signed,
			} )
		}
		
		return $rise_baza_patch_land_policies.get( key ) ?? prev
	}
	
	export function $rise_baza_patch_land_policy(
		link: $giper_baza_link,
		next?: $rise_baza_patch_land_policy,
	) {
		return $rise_baza_patch_land_policy_key( link.land().str || link.str, next )
	}
	
	export function $rise_baza_patch_land_policy_unsigned( key: string ) {
		return $rise_baza_patch_land_policy_key( key, { signed: false } )
	}
	
	export function $rise_baza_patch_parts_policy_detect( parts: $giper_baza_pack_parts ) {
		for( const [ land, part ] of parts ) {
			if( land === $rise_baza_patch_policy_fast_peer || land.startsWith( `${ $rise_baza_patch_policy_fast_peer }_` ) ) {
				$rise_baza_patch_land_policy_key( land, $rise_baza_patch_policy_fast )
				continue
			}
			if( !$rise_baza_patch_land_policy_key( land ).signed ) continue
			if( !part.units.length ) continue
			if( part.units.some( unit => unit instanceof $giper_baza_unit_seal ) ) continue
			$rise_baza_patch_land_policy_unsigned( land )
		}
	}
	
	export function $rise_baza_patch_sync_linked_fast_lands(
		Land: $giper_baza_land,
		units: readonly $giper_baza_unit[],
	) {
		for( const land of $rise_baza_patch_linked_fast_lands( Land, units ) ) {
			;( Land.$.$giper_baza_glob.yard() as $rise_baza_patch_yard ).sync_land( land )
		}
	}
	
	export function $rise_baza_patch_linked_fast_lands(
		Land: $giper_baza_land,
		units: readonly $giper_baza_unit[],
	) {
		const lands = new Map< string, $giper_baza_link >
		for( const unit of units ) {
			if( !( unit instanceof $giper_baza_unit_sand ) ) continue
			let value: $giper_baza_vary_type
			try {
				value = Land.sand_decode( unit )
			} catch( error ) {
				continue
			}
			if( !( value instanceof $giper_baza_link ) ) continue
			if( value.land().peer().str !== $rise_baza_patch_policy_fast_peer ) continue
			lands.set( value.land().str, value.land() )
		}
		return lands.values()
	}
	
	export function $rise_baza_patch_units_shell_empty(
		Land: $giper_baza_land,
		units: readonly $giper_baza_unit[],
	) {
		if( !units.length ) return false
		const schema = new Set([ 'Items', 'Children' ])
		
		for( const unit of units ) {
			if( !( unit instanceof $giper_baza_unit_sand ) ) return false
			if( unit.head().str ) return false
			if( unit.dead() ) return false
			
			try {
				const value = Land.sand_decode( unit )
				if( typeof value !== 'string' ) return false
				if( !schema.has( value ) ) return false
			} catch( error ) {
				return false
			}
		}
		
		return true
	}
	
	export function $rise_baza_patch_pawn_policy( Pawn: typeof $giper_baza_pawn ) {
		return ( Pawn as any ).land_policy as $rise_baza_patch_land_policy | undefined
	}
	
	export function $rise_baza_patch_pawn_fast( Pawn: typeof $giper_baza_pawn ) {
		const policy = $rise_baza_patch_pawn_policy( Pawn )
		if( !policy ) return false
		const next = {
			persistent: policy.persistent ?? true,
			signed: policy.signed ?? true,
		}
		return !next.persistent && !next.signed
	}
	
	export function $rise_baza_patch_link_make(
		context = $ as any,
		policy = $rise_baza_patch_policy_fast as $rise_baza_patch_land_policy,
	) {
		for( let i = 0; i < 4096; ++ i ) {
			const uuid = globalThis.crypto?.randomUUID?.()
				?? context.$mol_crypto_uuid?.call( context )
				?? $mol_crypto_uuid.call( $ as any )
			const hash = context.$giper_baza_link.hash_str( uuid )
			const persistent = policy.persistent ?? true
			const signed = policy.signed ?? true
			const lord_parts = context.$giper_baza_auth.current().pass().lord().str.split( '_' )
			while( lord_parts.length < 2 ) lord_parts.push( '' )
			const lord = lord_parts.slice( 0, 2 ).join( '_' )
			const area = hash.peer().str
			const link = persistent
				? new context.$giper_baza_link( `${ lord }_${ area }` )
				: signed
					? new context.$giper_baza_link( `${ lord }_${ $rise_baza_patch_policy_temp_signed_area_prefix }${ area.slice( 1 ) }` )
					: new context.$giper_baza_link( `${ $rise_baza_patch_policy_fast_peer }_${ hash.str }` )
			if( !$rise_baza_patch_land_policies.has( link.str ) ) return link
		}
		return $mol_fail( new Error( 'Too long fast land id generation' ) )
	}
	
	export function $rise_baza_patch_land_make< Pawn extends typeof $giper_baza_pawn >(
		Pawn: Pawn,
		context = $ as any,
	) {
		const policy = $rise_baza_patch_pawn_policy( Pawn ) ?? $rise_baza_patch_policy_fast
		const link = $rise_baza_patch_link_make( context, policy )
		$rise_baza_patch_land_policy( link, policy )
		const land = context.$giper_baza_glob.Land( link ) as $giper_baza_land
		land.Pawn( Pawn )
		return land
	}
	
	export function $rise_baza_patch_vary_fresh_links(
		vary: $giper_baza_vary_type,
		context = $ as any,
	): $giper_baza_vary_type {
		
		const Link = context.$giper_baza_link as typeof $giper_baza_link
		if( vary instanceof Link ) return new Link( vary.str )
		if( Array.isArray( vary ) ) return vary.map( item => $rise_baza_patch_vary_fresh_links( item, context ) )
		if( !vary || typeof vary !== 'object' ) return vary
		if( ArrayBuffer.isView( vary ) ) return vary
		if( vary instanceof $mol_time_moment ) return vary
		if( vary instanceof $mol_time_duration ) return vary
		if( vary instanceof $mol_time_interval ) return vary
		if( vary instanceof $mol_tree2 ) return vary
		if( typeof Element !== 'undefined' && vary instanceof Element ) return vary
		
		const fresh = {} as Record< string, $giper_baza_vary_type >
		const dict = vary as Readonly<{ [ key in string ]: $giper_baza_vary_type }>
		for( const key in dict ) fresh[ key ] = $rise_baza_patch_vary_fresh_links( dict[ key ], context )
		return fresh
		
	}
	
	export class $rise_baza_patch_mine extends $giper_baza_mine_temp {
		
		static mines = new Map< string, $rise_baza_patch_mine >()
		
		static override land( land: $giper_baza_link ) {
			let mine = this.mines.get( land.str )
			if( !mine ) {
				mine = this.make({ land: $mol_const( land ) }) as $rise_baza_patch_mine
				this.mines.set( land.str, mine )
			}
			return mine
		}
		
		units = new Set< $giper_baza_unit >()
		
		override units_save( diff: $giper_baza_mine_diff ) {
			for( const unit of diff.del ) this.units.delete( unit )
			for( const unit of diff.ins ) this.units.add( unit )
			for( const unit of diff.ins ) this.units_persisted.add( unit )
		}
		
		override units_load() {
			return [ ... this.units ]
		}
		
	}
	
	export class $rise_baza_patch_land extends $giper_baza_land {
		
		fast_payload_seen = false
		
		override Pawn< Pawn extends typeof $giper_baza_pawn >( Pawn: Pawn ) {
			const policy = $rise_baza_patch_pawn_policy( Pawn )
			if( policy ) {
				const next = $rise_baza_patch_land_policy( this.link(), policy )
				if( !next.signed ) this.seals_wipe()
			}
			return super.Pawn( Pawn )
		}
		
		seals_wipe() {
			for( const seal of [ ... this._seal_shot.values() ] ) this.seal_del( seal )
		}
		
		override mine() {
			if( $rise_baza_patch_land_policy( this.link() ).persistent ) return super.mine()
			return $rise_baza_patch_mine.land( this.link() )
		}
		
		override loading() {
			if( !$rise_baza_patch_land_policy( this.link() ).persistent ) {
				const units = this.mine().units_load()
				if( units.length ) this.diff_apply( units, 'skip_load' )
				return units
			}
			return super.loading()
		}
		
		override sync() {
			if( $rise_baza_patch_land_policy( this.link() ).persistent ) return super.sync()
			this.loading()
			this.bus()
			this.sync_yard()
			return this
		}
		
		override units_signing() {
			if( $rise_baza_patch_land_policy( this.link() ).signed ) return super.units_signing()
			return null
		}
		
		override units_saving() {
			const policy = $rise_baza_patch_land_policy( this.link() )
			if( policy.persistent && policy.signed ) return super.units_saving()
			
			this.units_signing()
			
			const mine = this.mine()
			const skip = new Set< $giper_baza_unit >()
			
			while( true ) {
				const snap = this.units_unsaved()
				const news = snap.filter( unit => !skip.has( unit ) )
				if( !news.length ) break
				
				const reaping = [ ... this.units_reaping ]
				this.units_reaping.clear()
				mine.units_save({ ins: news, del: reaping })
				
				for( const unit of news ) skip.add( unit )
			}
		}
		
		units_save_fast( units: readonly $giper_baza_unit[] ) {
			const policy = $rise_baza_patch_land_policy( this.link() )
			if( policy.persistent && policy.signed ) return
			
			if( !policy.signed ) this.units_signing()
			const mine = this.mine()
			
			const news = units.filter( unit => {
				if( mine.units_persisted.has( unit ) ) return false
				if( unit instanceof $giper_baza_auth_pass ) return policy.signed
				if( !( unit instanceof $giper_baza_unit_base ) ) return false
				return unit.encoded()
			} )
			const reaping = [ ... this.units_reaping ]
			this.units_reaping.clear()
			
			if( !news.length && !reaping.length ) return
			this.mine().units_save({ ins: news, del: reaping })
		}
		
		override sync_mine() {
			if( $rise_baza_patch_land_policy( this.link() ).persistent ) return super.sync_mine()
			return undefined
		}
		
		override diff_units( skip_faces = new $giper_baza_face_map ) {
			if( $rise_baza_patch_land_policy( this.link() ).signed ) {
				const units = super.diff_units( skip_faces )
				if( $rise_baza_patch_land_policy( this.link() ).persistent ) return units
				
				const passes = new Map< string, $giper_baza_auth_pass >()
				for( const unit of units ) {
					if( unit instanceof $giper_baza_auth_pass ) passes.set( unit.lord().str, unit )
				}
				
				for( const unit of units ) {
					if( unit instanceof $giper_baza_auth_pass ) continue
					if( passes.has( unit.lord().str ) ) continue
					const pass = this.lord_pass( unit.lord() )
					if( pass ) passes.set( unit.lord().str, pass )
				}
				
				return [ ... passes.values(), ... units.filter( unit => !( unit instanceof $giper_baza_auth_pass ) ) ]
			}
			
			// Server-side Glob.Land() may return a fresh instance for the same fast link.
			// Load transient mine units before collecting unsigned diffs for late readers.
			this.loading()
			this.units_signing()
			
			const delta = new Set< $giper_baza_unit_base >()
			
			const collect = ( unit: $giper_baza_unit_base )=> {
				const peer = unit.lord().peer().str
				const face_limit = skip_faces.get( peer )?.time_tick ?? 0
				if( unit.time_tick() > face_limit ) delta.add( unit )
			}
			
			for( const seal of this._seal_item.values() ) {
				if( seal.alive_items.size ) collect( seal )
			}
			
			for( const gift of this._gift.values() ) collect( gift )
			
			for( const kids of this._sand.values() ) {
				for( const peers of kids.values() ) {
					for( const sand of peers.values() ) {
						this.sand_load( sand )
						collect( sand )
					}
				}
			}
			
			return [ ... delta ]
		}
		
		override diff_apply( units: readonly $giper_baza_unit[], skip_load?: 'skip_load' ) {
			const policy = $rise_baza_patch_land_policy( this.link() )
			if( policy.signed ) {
				if( !policy.persistent && units.length ) this.fast_payload_seen = true
				const applied = super.diff_apply( units, skip_load )
				if( !policy.persistent && units.length && skip_load !== 'skip_load' ) {
					this.units_save_fast( units )
				}
				return applied
			}
			
			if( units.length === 0 ) return
			units = $mol_wire_sync( this.$ ).$giper_baza_unit_sort( units )
			
			for( const unit of units ) $giper_baza_unit_trusted_grant( unit )
			
			let applied = false
			
			for( const unit of units ) {
				if( unit instanceof $giper_baza_auth_pass ) continue
				
				switch( unit.kind() ) {
					case 'seal':
						const seal = unit as $giper_baza_unit_seal
						if( this._seal_shot.has( seal.shot().str ) ) continue
						applied = true
						this.seal_add( seal )
						break
					case 'gift':
						const gift = unit as $giper_baza_unit_gift
						if( $giper_baza_unit_gift.compare( this._gift.get( gift.mate().str ), gift ) <= 0 ) continue
						applied = true
						this.gift_add( gift )
						break
					case 'sand':
						const sand = unit as $giper_baza_unit_sand
						if( $giper_baza_unit_sand.compare( this.sand_get( sand.head(), sand.lord(), sand.self() ) ?? undefined, sand ) <= 0 ) continue
						applied = true
						this.sand_add( sand )
						break
					default:
						return this.$.$mol_fail( new Error( `Unsupported Kind` ) )
				}
			}
			
			if( applied && !$rise_baza_patch_units_shell_empty( this, units ) ) {
				this.fast_payload_seen = true
			}
			
			if( applied && skip_load !== 'skip_load' ) {
				if( !$rise_baza_patch_land_policy( this.link() ).persistent ) this.units_save_fast( units )
				this.broadcast()
			}
			return units
		}
		
		override sand_decode( sand: $giper_baza_unit_sand ): $giper_baza_vary_type {
			if( $rise_baza_patch_land_policy( this.link() ).signed ) return super.sand_decode( sand )
			if( sand._vary !== undefined ) return sand._vary
			const open = sand._open ?? sand.ball()
			sand._open = open
			return sand._vary = $giper_baza_link_base( this.link(), ()=> ( $giper_baza_vary.take( open ) as any )[0] )
		}
		
		override sands_open( sands: readonly $giper_baza_unit_sand[] ) {
			if( $rise_baza_patch_land_policy( this.link() ).signed ) return super.sands_open( sands )
			for( const sand of sands ) {
				if( sand._open ) continue
				sand._open = sand.ball()
			}
			return undefined
		}
		
		override post(
			lead: $giper_baza_link,
			head: $giper_baza_link,
			self: $giper_baza_link | null,
			vary: $giper_baza_vary_type,
			tag: keyof typeof $giper_baza_unit_sand_tag = 'term',
		) {
			vary = $rise_baza_patch_vary_fresh_links( vary, this.$ )
			
			if( $rise_baza_patch_land_policy( this.link() ).signed ) {
				return super.post( lead, head, self, vary, tag )
			}
			
			let open = $giper_baza_link_base( this.link(), ()=> vary === null ? new Uint8Array([ 78 ]) : $giper_baza_vary.pack([ vary ]) )
			
			const sand = $giper_baza_unit_sand.make( open.byteLength, tag )
			sand._open = open
			sand.ball( open )
			sand._land = this
			
			$giper_baza_unit_trusted_grant( sand )
			
			sand.lord( this.$.$giper_baza_auth.current().pass().lord() )
			sand.lead( lead )
			sand.head( head )
			sand._vary = vary
			sand.self( self ?? this.self_make( $mol_hash_numbers( open, sand.idea_seed() ) ) )
			
			const tick = this.faces.tick().time_tick
			const prev = this.sand_get( sand.head(), sand.lord(), sand.self() )
			sand.time_tick( Math.max( tick, ( prev?.time_tick() ?? 0 ) + 1 ) )
			
			this.diff_apply( [ sand ] )
			return sand
		}
		
		/*override diff_apply( units: readonly $giper_baza_unit[], skip_load?: 'skip_load' ) {
			if( !$rise_baza_patch_land_policy( this.link() ).signed ) {
				for( const unit of units ) $giper_baza_unit_trusted_grant( unit )
			}
			return super.diff_apply( units, skip_load )
		}*/
		
		override lord_rank( lord: $giper_baza_link | null, next?: typeof $giper_baza_rank.Value ) {
			if( !$rise_baza_patch_land_policy( this.link() ).signed && next === undefined ) {
				return $giper_baza_rank_rule
			}
			return super.lord_rank( lord, next )
		}
		
		override broadcast() {
			const policy = $rise_baza_patch_land_policy( this.link() )
			if( policy.persistent ) return super.broadcast()
			if( !this.fast_payload_seen ) return
			
			super.broadcast()
			const yard = this.$.$giper_baza_glob.yard() as $rise_baza_patch_yard
			yard.sync_news_later?.()
		}
		
		fast_shell_empty() {
			const units = this.diff_units( new $giper_baza_face_map )
			return $rise_baza_patch_units_shell_empty( this, units )
		}
		
	}
	
	export class $rise_baza_patch_yard extends $giper_baza_yard {
		
		sync_news_scheduled = false
		sync_lands_wanted = new Set< string >
		sync_lands_wanted_scheduled = false
		master_port = null as $mol_rest_port | null
		master_port_pending = null as Promise< $mol_rest_port | null > | null
		
		master_port_send( task: ( port: $mol_rest_port )=> void ) {
			const socket = ( this.master_port as any )?.socket
			if( this.master_port && ( socket?.readyState === undefined || socket.readyState === socket.OPEN ) ) {
				task( this.master_port )
				return true
			}
			
			if( this.master_port_pending ) {
				this.master_port_pending.then(
					port => {
						if( port ) task( port )
					},
					error => $mol_fail_log( error ),
				)
				return true
			}
			
			this.master_port_pending = (
				$mol_wire_async( this ).master() as Promise< $mol_rest_port | null >
			).then(
				port => {
					this.master_port_pending = null
					if( !port ) return null
					this.master_port = port
					task( port )
					return port
				},
				error => {
					this.master_port_pending = null
					$mol_fail_log( error )
					return null
				},
			)
			return true
		}
		
		override masters() {
			const socket = ( this.master_port as any )?.socket
			if( this.master_port && ( socket?.readyState === undefined || socket.readyState === socket.OPEN ) ) {
				return [ this.master_port ]
			}
			return [] as $mol_rest_port[]
		}
		
		override ports() {
			return [ ... this.masters(), ... this.slaves ]
		}
		
		sync_news_later() {
			if( this.sync_news_scheduled ) return
			this.sync_news_scheduled = true
			new this.$.$mol_after_timeout( 0, ()=> {
				this.sync_news_scheduled = false
				$mol_wire_async( ()=> this.sync_news() )()
			} )
		}
		
		sync_lands_wanted_later() {
			if( this.sync_lands_wanted_scheduled ) return
			this.sync_lands_wanted_scheduled = true
			new this.$.$mol_after_timeout( 0, ()=> {
				this.sync_lands_wanted_scheduled = false
				$mol_wire_async( ()=> this.sync_lands_wanted_now() )()
			} )
		}
		
		masters_ready() {
			const master = this.master() as $mol_rest_port | Promise< $mol_rest_port > | null
			if( master instanceof Promise ) throw master
			
			const socket = ( master as any )?.socket
			if( socket?.readyState !== undefined && socket.readyState !== socket.OPEN ) {
				throw new Promise< void >( ( done, fail )=> {
					if( socket.readyState === socket.OPEN ) return done()
					if( socket.addEventListener ) {
						socket.addEventListener( 'open', ()=> done(), { once: true } )
						socket.addEventListener( 'error', ()=> fail( new Error( 'Master socket error' ) ), { once: true } )
						return
					}
					if( socket.once ) {
						socket.once( 'open', ()=> done() )
						socket.once( 'error', fail )
					}
				} )
			}
			
			return master ? [ master ] : []
		}
		
		sync_lands_wanted_now() {
			if( !this.sync_lands_wanted.size ) return
			
			const lands = [ ... this.sync_lands_wanted ]
			if( !this.master_port_send( port => {
				for( const land of lands ) {
					const link = new $giper_baza_link( land )
					this.port_lands_passive( port ).add( land )
					this.pull_port_land([ port, link ])
					this.sync_lands_wanted.delete( land )
				}
			} ) ) return
		}
		
		override sync_news() {
			const glob = this.$.$giper_baza_glob
			const links = [ ... this.lands_news ]
			const lands = links.map( link => glob.Land( new $giper_baza_link( link ) ) )
			if( !lands.length ) return
			
			for( const link of links ) this.lands_news.delete( link )
			if( !this.master_port_send( port => {
				for( const land of lands ) this.sync_port_land([ port, land.link() ])
				if( this.lands_news.size ) this.sync_news_later()
			} ) ) {
				for( const link of links ) this.lands_news.add( link )
			}
		}
		
		override port_income( port: $mol_rest_port, msg: Uint8Array< ArrayBuffer > ) {
			const pack = $mol_wire_sync( $giper_baza_pack ).from( msg ) as $giper_baza_pack
			const parts = $mol_wire_sync( pack ).parts()
			$rise_baza_patch_parts_policy_detect( parts )
			const res = super.port_income( port, msg )
			for( const [ land, part ] of parts ) {
				const Land = this.$.$giper_baza_glob.Land( new $giper_baza_link( land ) ) as $rise_baza_patch_land
				const policy = $rise_baza_patch_land_policy( Land.link() )
				if( part.units.length && !policy.persistent ) $rise_baza_patch_sync_linked_fast_lands( Land, part.units )
				if( !part.units.length && part.faces.size && !policy.persistent ) {
					if( policy.signed ) this.sync_port_land([ port, Land.link() ])
					else this.sync_port_land_full([ port, Land.link() ])
				}
				if( policy.persistent || !policy.signed ) continue
				if( !part.units.length ) continue
				Land.units_save_fast( part.units )
				Land.broadcast()
			}
			return res
		}
		
		override sync_land( land: $giper_baza_link ) {
			if( !$rise_baza_patch_land_policy( land ).persistent ) {
				// Keep the request queued until a pull is actually sent to an open master.
				this.sync_lands_wanted.add( land.str )
				if( !this.master_port_send( port => {
					this.port_lands_passive( port ).add( land.str )
					this.pull_port_land([ port, land ])
					this.sync_lands_wanted.delete( land.str )
				} ) ) {
					this.sync_lands_wanted_later()
				}
				return
			}
			return super.sync_land( land )
		}
		
		override sync_port_land( [ port, land ]: [ $mol_rest_port, $giper_baza_link ] ) {
			const policy = $rise_baza_patch_land_policy( land )
			if( policy.persistent ) return super.sync_port_land([ port, land ])
			
			try {
				const Land = this.$.$giper_baza_glob.Land( land )
				const first = !this.face_port_land([ port, land ])
				if( first ) {
					this.face_port_land([ port, land ], new $giper_baza_face_map )
				}
				
				const faces = this.face_port_land([ port, land ])
				if( !faces ) return
				
				const part = Land.diff_part( policy.signed ? faces : new $giper_baza_face_map )
				if( !part.units.length ) {
					if( !first ) return
					const pack = $giper_baza_pack.make([[ Land.link().str, part ]])
					port.send_bin( pack.asArray() )
					return
				}
				
				const pack = $giper_baza_pack.make([[ Land.link().str, part ]])
				port.send_bin( pack.asArray() )
				faces.sync( part.faces )
			} catch( error ) {
				if( error instanceof Promise ) error.then(
					()=> this.sync_port_land([ port, land ]),
					error => $mol_fail_log( error ),
				)
				else $mol_fail_log( error )
			}
		}
		
		pull_port_land( [ port, land ]: [ $mol_rest_port, $giper_baza_link ] ) {
			const policy = $rise_baza_patch_land_policy( land )
			if( policy.persistent ) return this.sync_port_land([ port, land ])
			
			try {
				const Land = this.$.$giper_baza_glob.Land( land )
				const faces = policy.signed ? Land.faces.clone() : new $giper_baza_face_map
				if( !faces.size ) faces.peer_time( land.peer().str, 0, 0 )
				const part = new $giper_baza_pack_part( [], faces )
				port.send_bin( $giper_baza_pack.make([[ Land.link().str, part ]]).asArray() )
			} catch( error ) {
				if( error instanceof Promise ) error.then(
					()=> this.pull_port_land([ port, land ]),
					error => $mol_fail_log( error ),
				)
				else $mol_fail_log( error )
			}
		}
		
		sync_port_land_full( [ port, land ]: [ $mol_rest_port, $giper_baza_link ] ) {
			const policy = $rise_baza_patch_land_policy( land )
			if( policy.persistent || policy.signed ) return this.sync_port_land([ port, land ])
			
			try {
				const Land = this.$.$giper_baza_glob.Land( land )
				if( !this.face_port_land([ port, land ]) ) {
					this.face_port_land([ port, land ], new $giper_baza_face_map )
				}
				const faces = this.face_port_land([ port, land ])
				if( !faces ) return
				
				const part = Land.diff_part( new $giper_baza_face_map )
				if( !part.units.length ) return
				
				port.send_bin( $giper_baza_pack.make([[ Land.link().str, part ]]).asArray() )
				faces.sync( part.faces )
			} catch( error ) {
				if( error instanceof Promise ) error.then(
					()=> this.sync_port_land_full([ port, land ]),
					error => $mol_fail_log( error ),
				)
				else $mol_fail_log( error )
			}
		}
		
	}
	
	export function $rise_baza_patch_atom_link_to( context = $ as any ) {
		const AtomLink = context.$giper_baza_atom_link
		const proto = AtomLink as any
		if( proto.$rise_baza_patch_atom_link_to ) return
		proto.$rise_baza_patch_atom_link_to = true
		
		const to_orig = AtomLink.to
		const cache = new WeakMap< object, any >()
		
		AtomLink.to = function( Value: any ) {
			const Base = to_orig.call( this, Value )
			const cached = cache.get( Base )
			if( cached ) return cached
			
			class $rise_baza_patch_atom_link_to extends Base {
				
				@ $mol_action
				ensure_lord( peer: $giper_baza_link | null, preset: $giper_baza_rank_preset ) {
					const Pawn = Value() as typeof $giper_baza_pawn
					if( !$rise_baza_patch_pawn_fast( Pawn ) ) return super.ensure_lord( peer, preset )
					
					const land = $rise_baza_patch_land_make( Pawn, this.$ )
					const pawn = land.Data( Pawn )
					if( Pawn.meta ) pawn.meta( Pawn.meta )
					this.val_of( peer, pawn.link() )
				}
				
			}
			
			cache.set( Base, $rise_baza_patch_atom_link_to )
			return $rise_baza_patch_atom_link_to
		}
	}
	
	export function $rise_baza_patch_list_link_to( context = $ as any ) {
		const ListLink = context.$giper_baza_list_link
		const proto = ListLink as any
		if( proto.$rise_baza_patch_list_link_to ) return
		proto.$rise_baza_patch_list_link_to = true
		
		const to_orig = ListLink.to
		const cache = new WeakMap< object, any >()
		
		ListLink.to = function( Value: any ) {
			const Base = to_orig.call( this, Value )
			const cached = cache.get( Base )
			if( cached ) return cached
			
			class $rise_baza_patch_list_link_to extends Base {
				
				remote_list( next?: readonly $giper_baza_pawn[] ) {
					const Pawn = Value() as typeof $giper_baza_pawn
					if( next !== undefined || !$rise_baza_patch_pawn_fast( Pawn ) ) return super.remote_list( next ) as any
					
					return this.items_vary()
						.filter( ( value: $giper_baza_vary_type )=> value instanceof this.$.$giper_baza_link )
						.map( ( link: $giper_baza_link )=> this.$.$giper_baza_glob.Pawn( link, Pawn ) ) as any
				}
				
				@ $mol_action
				make( config: null | number | $giper_baza_rank_preset | $giper_baza_land ) {
					const Pawn = Value() as typeof $giper_baza_pawn
					
					if(
						config
						&& typeof config !== 'number'
						&& !( config instanceof this.$.$giper_baza_land )
						&& $rise_baza_patch_pawn_fast( Pawn )
					) {
						const land = $rise_baza_patch_land_make( Pawn, this.$ )
						this.splice([ land.link() ])
						const pawn = land.Pawn( Pawn ).Data()
						if( Pawn.meta ) pawn.meta( Pawn.meta )
						return pawn as any
					}
					
					return super.make( config ) as any
				}
				
			}
			
			cache.set( Base, $rise_baza_patch_list_link_to )
			return $rise_baza_patch_list_link_to
		}
	}
	
	export function $rise_baza_patch_apply( context = $ as any ) {
		if(
			context.$giper_baza_land === $rise_baza_patch_land
			&& context.$giper_baza_yard === $rise_baza_patch_yard
		) return {}
		
		$rise_baza_patch_atom_link_to( context )
		$rise_baza_patch_list_link_to( context )
		
		const patch = {
			$giper_baza_land: $rise_baza_patch_land,
			$giper_baza_yard: $rise_baza_patch_yard,
		}
		Object.assign( context, patch )
		return patch
	}
	
}
